import React, { useState, useEffect } from 'react';
import { MessageSquare, Clock, User, Mail, Phone, X, Search, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserRequestModal = ({ isOpen, onClose, onUpdate }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [stats, setStats] = useState({
    totalInquiries: 0,
    newInquiries: 0,
    inProgressInquiries: 0,
    respondedInquiries: 0
  });

  useEffect(() => {
    if (isOpen) {
      fetchInquiries();
    }
  }, [isOpen]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      // Enhanced query to get ALL property inquiries with complete details
      const { data, error } = await supabase?.from('property_inquiries')
        ?.select(`
          *,
          client:user_profiles!client_id(full_name, email, phone),
          property:properties(
            title, 
            address, 
            price, 
            property_type,
            commune:communes(name, district)
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors du chargement des demandes:', error);
        throw error;
      }

      const allInquiries = data || [];
      
      // Calculate detailed statistics
      const statsData = {
        totalInquiries: allInquiries?.length,
        newInquiries: allInquiries?.filter(i => i?.status === 'new')?.length,
        inProgressInquiries: allInquiries?.filter(i => i?.status === 'in_progress')?.length,
        respondedInquiries: allInquiries?.filter(i => i?.status === 'responded')?.length
      };

      console.log('📊 User request statistics loaded:', statsData);
      setInquiries(allInquiries);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      alert('Erreur lors du chargement des demandes.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      const { error } = await supabase?.from('property_inquiries')
        ?.update({ 
          status: newStatus,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', inquiryId);

      if (error) throw error;
      
      fetchInquiries();
      onUpdate?.();
      
      // Show success message
      const statusMessages = {
        'in_progress': 'Demande marquée en cours de traitement',
        'responded': 'Demande marquée comme répondue',
        'closed': 'Demande marquée comme fermée',
        'new': 'Demande remise en attente'
      };
      
      alert(statusMessages?.[newStatus] || 'Statut mis à jour avec succès.');
    } catch (error) {
      console.error('Error updating inquiry status:', error);
      alert('Erreur lors de la mise à jour du statut.');
    }
  };

  const handleRespond = async (inquiryId) => {
    if (!responseText?.trim()) {
      alert('Veuillez saisir une réponse.');
      return;
    }

    try {
      const { error } = await supabase?.from('property_inquiries')
        ?.update({ 
          agent_response: responseText,
          status: 'responded',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', inquiryId);

      if (error) throw error;
      
      setResponseText('');
      setSelectedInquiry(null);
      fetchInquiries();
      onUpdate?.();
      
      alert('Réponse envoyée avec succès.');
    } catch (error) {
      console.error('Error sending response:', error);
      alert('Erreur lors de l\'envoi de la réponse.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-orange-100 text-orange-800', text: 'Nouvelle', icon: AlertCircle },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', text: 'En cours', icon: Clock },
      responded: { color: 'bg-green-100 text-green-800', text: 'Traitée', icon: CheckCircle },
      closed: { color: 'bg-gray-100 text-gray-800', text: 'Fermée', icon: CheckCircle }
    };

    const config = statusConfig?.[status] || statusConfig?.['new'];
    const IconComponent = config?.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <IconComponent className="h-3 w-3" />
        {config?.text}
      </span>
    );
  };

  const filteredInquiries = inquiries?.filter(inquiry => {
    const matchesSearch = inquiry?.message?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         inquiry?.client?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         inquiry?.property?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inquiry?.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose}></div>
        
        <div className="relative w-full max-w-7xl bg-white rounded-lg shadow-xl max-h-screen overflow-hidden">
          {/* Enhanced Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Centre de Gestion des Demandes Clients
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Toutes les demandes de renseignements reçues depuis le frontend
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchInquiries}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Actualiser
              </Button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Statistics Dashboard */}
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <p className="text-2xl font-bold text-blue-600">{stats?.totalInquiries}</p>
                <p className="text-sm text-gray-600">Total Demandes</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <p className="text-2xl font-bold text-orange-600">{stats?.newInquiries}</p>
                <p className="text-sm text-gray-600">Nouvelles</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats?.inProgressInquiries}</p>
                <p className="text-sm text-gray-600">En Cours</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <p className="text-2xl font-bold text-green-600">{stats?.respondedInquiries}</p>
                <p className="text-sm text-gray-600">Traitées</p>
              </div>
            </div>

            {/* Enhanced Info Panel */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <MessageSquare className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">Comment fonctionnent les demandes ?</h4>
                  <p className="text-sm text-blue-700">
                    Les demandes sont créées automatiquement lorsque les clients utilisent les formulaires 
                    "Demander des informations" sur les pages individuelles des propriétés. 
                    Chaque demande contient les détails du client, la propriété concernée et leur message.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Rechercher par client, propriété ou message..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e?.target?.value)}
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="new">Nouvelles</option>
                    <option value="in_progress">En cours</option>
                    <option value="responded">Traitées</option>
                    <option value="closed">Fermées</option>
                  </Select>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                Affichage: {filteredInquiries?.length} / {inquiries?.length} demandes
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto max-h-96">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="text-gray-600 mt-4 ml-4">Chargement des demandes...</p>
              </div>
            ) : filteredInquiries?.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {inquiries?.length === 0 ? 'Aucune demande reçue' : 'Aucune demande trouvée'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {inquiries?.length === 0 
                    ? 'Les demandes des clients apparaîtront ici automatiquement'
                    : searchTerm || statusFilter !== 'all' ? 'Essayez de modifier vos critères de recherche' : 'Aucune demande ne correspond aux filtres actuels'
                  }
                </p>
                {inquiries?.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-sm text-yellow-800">
                      <strong>💡 Astuce:</strong> Pour tester, visitez une page de propriété individuelle 
                      et utilisez le formulaire "Demander des informations" pour créer une demande.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {filteredInquiries?.map((inquiry) => (
                  <div
                    key={inquiry?.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white"
                  >
                    {/* Request Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {inquiry?.client?.full_name}
                          </span>
                        </div>
                        {getStatusBadge(inquiry?.status)}
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(inquiry?.created_at)?.toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(inquiry?.created_at)?.toLocaleTimeString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    {/* Property and Client Information */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                      {/* Property Details */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Propriété Concernée
                        </h4>
                        <div className="space-y-2">
                          <p className="font-medium text-gray-900">{inquiry?.property?.title}</p>
                          <p className="text-sm text-gray-600">{inquiry?.property?.address}</p>
                          {inquiry?.property?.commune && (
                            <p className="text-sm text-gray-600">
                              {inquiry?.property?.commune?.name}, {inquiry?.property?.commune?.district}
                            </p>
                          )}
                          <p className="text-lg font-semibold text-blue-600">
                            {formatCurrency(inquiry?.property?.price)}
                          </p>
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                            {inquiry?.property?.property_type}
                          </span>
                        </div>
                      </div>
                      
                      {/* Client Details */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Informations Client
                        </h4>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span>{inquiry?.client?.email}</span>
                          </div>
                          {inquiry?.client_phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span>{inquiry?.client_phone}</span>
                            </div>
                          )}
                          {inquiry?.preferred_contact_time && (
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="h-4 w-4 text-gray-400" />
                              <span>Préférence: {inquiry?.preferred_contact_time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Request Message */}
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-900 mb-2">Message du Client:</h5>
                      <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {inquiry?.message}
                        </p>
                      </div>
                    </div>

                    {/* Agent Response */}
                    {inquiry?.agent_response && (
                      <div className="mb-4">
                        <h5 className="font-medium text-gray-900 mb-2">Réponse de l'Agent:</h5>
                        <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {inquiry?.agent_response}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
                      {inquiry?.status === 'new' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatusChange(inquiry?.id, 'in_progress')}
                          >
                            <Clock className="h-4 w-4 mr-1" />
                            Marquer en cours
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setSelectedInquiry(inquiry)}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Répondre
                          </Button>
                        </>
                      )}
                      
                      {inquiry?.status === 'in_progress' && (
                        <Button
                          size="sm"
                          onClick={() => setSelectedInquiry(inquiry)}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Répondre
                        </Button>
                      )}
                      
                      {inquiry?.status === 'responded' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusChange(inquiry?.id, 'closed')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Clôturer
                        </Button>
                      )}
                      
                      <Select
                        value={inquiry?.status}
                        onChange={(e) => handleStatusChange(inquiry?.id, e?.target?.value)}
                        className="text-sm min-w-[140px]"
                      >
                        <option value="new">Nouvelle</option>
                        <option value="in_progress">En cours</option>
                        <option value="responded">Traitée</option>
                        <option value="closed">Fermée</option>
                      </Select>
                      
                      <div className="ml-auto text-xs text-gray-500">
                        ID: {inquiry?.id?.substring(0, 8)}...
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Response Modal */}
      {selectedInquiry && (
        <div className="fixed inset-0 z-60 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50"></div>
            
            <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-2xl">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Répondre à {selectedInquiry?.client?.full_name}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedInquiry(null);
                      setResponseText('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Request Context */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Contexte de la demande:</h4>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Propriété:</strong> {selectedInquiry?.property?.title}
                  </p>
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Prix:</strong> {formatCurrency(selectedInquiry?.property?.price)}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Message original:</strong> {selectedInquiry?.message}
                  </p>
                </div>
                
                {/* Response Form */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Votre réponse détaillée
                  </label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e?.target?.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Rédigez votre réponse professionnelle au client..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Caractères: {responseText?.length} / 1000 recommandés
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedInquiry(null);
                      setResponseText('');
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => handleRespond(selectedInquiry?.id)}
                    disabled={!responseText?.trim()}
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Envoyer la Réponse
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRequestModal;